/**
 * @file Resolves the scope labels shown in the UI from the stored
 * `parameters/analyticsLabels` document. Consumers: `useAnalyticsScopeLabels`
 * and the Scope labels section's draft seed. Each field falls back on its own:
 * a label that is missing, not a string, or blank after trimming shows its
 * default ("My sales", "My team", "Org overall", "Team", "teams",
 * "My sales credit", "Mine"), and the others keep theirs. The Org sub-label
 * has no default and resolves to the empty string, which renders nothing.
 */
import { ANALYTICS_DEFAULT_TEAM_NOUN } from '#types';
import type { AnalyticsScopeLabels } from '#types';

const DEFAULT_SCOPE_LABELS: Required<AnalyticsScopeLabels> = {
  self: 'My sales',
  team: 'My team',
  org: 'Org overall',
  teamNoun: ANALYTICS_DEFAULT_TEAM_NOUN,
  teamNounPlural: 'teams',
  selfHome: 'My sales credit',
  selfProjects: 'Mine',
  orgSubLabel: '',
};

export const analyticsScopeLabels = (
  stored: Readonly<Partial<AnalyticsScopeLabels>> | null | undefined,
): Required<AnalyticsScopeLabels> => {
  const labelFor = (field: keyof AnalyticsScopeLabels): string => {
    const label: unknown = stored?.[field];
    return typeof label === 'string' && label.trim() !== ''
      ? label
      : DEFAULT_SCOPE_LABELS[field];
  };
  return {
    self: labelFor('self'),
    team: labelFor('team'),
    org: labelFor('org'),
    teamNoun: labelFor('teamNoun'),
    teamNounPlural: labelFor('teamNounPlural'),
    selfHome: labelFor('selfHome'),
    selfProjects: labelFor('selfProjects'),
    orgSubLabel: labelFor('orgSubLabel'),
  };
};
